// src/store/reports/reportsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';
import { errorMessage } from '../errorMessage';

// Async thunks
export const fetchNearExpiry = createAsyncThunk(
  'reports/fetchNearExpiry',
  async (days, { rejectWithValue }) => {
    try {
      const response = await apiService.getNearExpiry(days);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchCustomerStatusBreakdown = createAsyncThunk(
  'reports/fetchCustomerStatusBreakdown',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getCustomerStatusBreakdown(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchRecentCustomers = createAsyncThunk(
  'reports/fetchRecentCustomers',
  async (limit, { rejectWithValue }) => {
    try {
      const response = await apiService.getRecentCustomers(limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchDistributorCounts = createAsyncThunk(
  'reports/fetchDistributorCounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getDistributorCustomerCounts();
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchSalesMetrics = createAsyncThunk(
  'reports/fetchSalesMetrics',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getSalesMetrics(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchSalesScoreboard = createAsyncThunk(
  'reports/fetchSalesScoreboard',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getSalesScoreboard(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

// Initial state
const initialState = {
  nearExpiry: { days: 30, today: null, results: [] },
  metrics: null,
  scoreboard: [],
  isNearExpiryLoading: false,
  isMetricsLoading: false,
  isScoreboardLoading: false,
  // Per-concern error fields: the Sales Reports screen dispatches
  // fetchSalesMetrics and fetchSalesScoreboard together, so a single shared
  // `error` field would let one thunk's failure be shown against both
  // sections (or get silently overwritten by the other). Each thunk below
  // only ever writes its own field.
  nearExpiryError: null,
  metricsError: null,
  scoreboardError: null,

  // Dashboard aggregates (Phase 3). The dashboard mounts the status chart and
  // the recent-customers widget together, which is why these get their own
  // loading and error fields too.
  statusBreakdown: [],
  recentCustomers: [],
  distributorCounts: { results: [], unassigned_count: 0 },
  isStatusBreakdownLoading: false,
  isRecentCustomersLoading: false,
  isDistributorCountsLoading: false,
  statusBreakdownError: null,
  recentCustomersError: null,
  distributorCountsError: null
};

// Create slice
const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearNearExpiryError: (state) => {
      state.nearExpiryError = null;
    },
    clearMetricsError: (state) => {
      state.metricsError = null;
    },
    clearScoreboardError: (state) => {
      state.scoreboardError = null;
    },
    clearStatusBreakdownError: (state) => {
      state.statusBreakdownError = null;
    },
    clearRecentCustomersError: (state) => {
      state.recentCustomersError = null;
    },
    clearDistributorCountsError: (state) => {
      state.distributorCountsError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchNearExpiry
      .addCase(fetchNearExpiry.pending, (state) => {
        state.isNearExpiryLoading = true;
        state.nearExpiryError = null;
      })
      .addCase(fetchNearExpiry.fulfilled, (state, action) => {
        state.isNearExpiryLoading = false;
        state.nearExpiry = action.payload;
      })
      .addCase(fetchNearExpiry.rejected, (state, action) => {
        state.isNearExpiryLoading = false;
        state.nearExpiryError = action.payload;
      })

      // fetchSalesMetrics
      .addCase(fetchSalesMetrics.pending, (state) => {
        state.isMetricsLoading = true;
        state.metricsError = null;
      })
      .addCase(fetchSalesMetrics.fulfilled, (state, action) => {
        state.isMetricsLoading = false;
        state.metrics = action.payload;
      })
      .addCase(fetchSalesMetrics.rejected, (state, action) => {
        state.isMetricsLoading = false;
        state.metricsError = action.payload;
        // Clear the previous period's figures so they can never sit on
        // screen under new, rejected date inputs (e.g. a >24 month range
        // parse_date_range rejects with 400) - SalesReports renders
        // `metrics` unconditionally once loading ends.
        state.metrics = null;
      })

      // fetchSalesScoreboard
      .addCase(fetchSalesScoreboard.pending, (state) => {
        state.isScoreboardLoading = true;
        state.scoreboardError = null;
      })
      .addCase(fetchSalesScoreboard.fulfilled, (state, action) => {
        state.isScoreboardLoading = false;
        state.scoreboard = action.payload.results;
      })
      .addCase(fetchSalesScoreboard.rejected, (state, action) => {
        state.isScoreboardLoading = false;
        state.scoreboardError = action.payload;
        // Same reasoning as fetchSalesMetrics.rejected above: don't leave
        // a stale scoreboard on screen under a different, rejected range.
        state.scoreboard = [];
      })

      // fetchCustomerStatusBreakdown
      .addCase(fetchCustomerStatusBreakdown.pending, (state) => {
        state.isStatusBreakdownLoading = true;
        state.statusBreakdownError = null;
      })
      .addCase(fetchCustomerStatusBreakdown.fulfilled, (state, action) => {
        state.isStatusBreakdownLoading = false;
        // The endpoint wraps the rows; the widget maps over them directly.
        state.statusBreakdown = action.payload.results;
      })
      .addCase(fetchCustomerStatusBreakdown.rejected, (state, action) => {
        state.isStatusBreakdownLoading = false;
        state.statusBreakdownError = action.payload;
        // Never leave a stale chart on screen after a failed refresh.
        state.statusBreakdown = [];
      })

      // fetchRecentCustomers
      .addCase(fetchRecentCustomers.pending, (state) => {
        state.isRecentCustomersLoading = true;
        state.recentCustomersError = null;
      })
      .addCase(fetchRecentCustomers.fulfilled, (state, action) => {
        state.isRecentCustomersLoading = false;
        state.recentCustomers = action.payload.results;
      })
      .addCase(fetchRecentCustomers.rejected, (state, action) => {
        state.isRecentCustomersLoading = false;
        state.recentCustomersError = action.payload;
        state.recentCustomers = [];
      })

      // fetchDistributorCounts
      .addCase(fetchDistributorCounts.pending, (state) => {
        state.isDistributorCountsLoading = true;
        state.distributorCountsError = null;
      })
      .addCase(fetchDistributorCounts.fulfilled, (state, action) => {
        state.isDistributorCountsLoading = false;
        // Whole payload here, unlike the two above: the filter needs
        // `unassigned_count` alongside `results`.
        state.distributorCounts = action.payload;
      })
      .addCase(fetchDistributorCounts.rejected, (state, action) => {
        state.isDistributorCountsLoading = false;
        state.distributorCountsError = action.payload;
        state.distributorCounts = { results: [], unassigned_count: 0 };
      });
  }
});

export const {
  clearNearExpiryError,
  clearMetricsError,
  clearScoreboardError,
  clearStatusBreakdownError,
  clearRecentCustomersError,
  clearDistributorCountsError
} = reportsSlice.actions;
export default reportsSlice.reducer;
